import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { get, put } from "src/utils/networkUtils";
import Iconify from "../../components/iconify";
import InfiniteScroll from "react-infinite-scroller";
import FptsLineupsChart from "./FptsLineupsChart";
import VersusGameLineupDialog from "./VersusGameLineupDialog";
import UserCardsDialog from "./UserCardsDialog";
import FptsStatsSummary from "./FptsStatsSummary";
import FilterPanel from "./FilterPanel";
import VersusLineupsTableContent from "./VersusLineupsTableContent";

const FptsMax = 400;
export default function VersusLineupsTable({ isActive = false }) {
  const [lineups, setLineups] = useState([]);
  const [lineupsLoading, setLineupsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreLineups, setHasMoreLineups] = useState(true);
  const [toggleIgnoredLoading, setToggleIgnoredLoading] = useState(null);
  const [fptsStats, setFptsStats] = useState(null);
  const [fptsStatsLoading, setFptsStatsLoading] = useState(false);

  const [filters, setFilters] = useState({
    fptsRange: [0, FptsMax],
    orderBy: "createdAt",
    orderDirection: "DESC",
  });

  const [filterInputs, setFilterInputs] = useState({
    fptsMin: 0,
    fptsMax: FptsMax,
  });

  const [filterPanelExpanded, setFilterPanelExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLineupId, setSelectedLineupId] = useState(null);
  const [cardsDialogOpen, setCardsDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState(null);

  const loadLineups = useCallback(
    async (page) => {
      try {
        if (page === 0) {
          setLineupsLoading(true);
        } else {
          setLoadingMore(true);
        }

        const offset = page * 100;

        const queryParams = new URLSearchParams({
          offset: offset.toString(),
          limit: "100",
          orderBy: filters.orderBy,
          orderDirection: filters.orderDirection,
        });

        if (filters.fptsRange[0] > 0) {
          queryParams.append("fptsMin", filters.fptsRange[0].toString());
        }

        if (filters.fptsRange[1] < FptsMax) {
          queryParams.append("fptsMax", filters.fptsRange[1].toString());
        }

        const response = await get(
          `/admin/versus/lineups?${queryParams.toString()}`
        );

        if (response.lineups && response.lineups.length > 0) {
          setLineups((prevLineups) => [...prevLineups, ...response.lineups]);
        } else {
          setHasMoreLineups(false);
        }

        setLineupsLoading(false);
        setLoadingMore(false);
        return true;
      } catch (error) {
        console.error("Error loading lineups:", error);
        setLineupsLoading(false);
        setLoadingMore(false);
        return false;
      }
    },
    [filters.orderBy, filters.orderDirection, filters.fptsRange]
  );

  const loadFptsStats = useCallback(async () => {
    try {
      setFptsStatsLoading(true);
      const response = await get("/admin/versus/stats/fpts");

      if (response.success && response.data) {
        setFptsStats(response.data);
      } else {
        console.error("Failed to load FPTS statistics:", response.error);
      }
    } catch (error) {
      console.error("Error loading FPTS statistics:", error);
    } finally {
      setFptsStatsLoading(false);
    }
  }, []);

  const handleInputChange = (field, value) => {
    const numValue =
      value === "" ? 0 : Math.max(0, Math.min(FptsMax, parseInt(value) || 0));
    setFilterInputs((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const handleApplyFilters = () => {
    const min = Math.min(filterInputs.fptsMin, filterInputs.fptsMax);
    const max = Math.max(filterInputs.fptsMin, filterInputs.fptsMax);

    setFilters((prev) => ({
      ...prev,
      fptsRange: [min, max],
    }));

    setFilterInputs({ fptsMin: min, fptsMax: max });
  };

  const handleClearFilters = () => {
    setFilterInputs({ fptsMin: 0, fptsMax: FptsMax });
    setFilters((prev) => ({
      ...prev,
      fptsRange: [0, FptsMax],
    }));
  };

  const handleSort = (column) => {
    const newDirection =
      filters.orderBy === column && filters.orderDirection === "DESC"
        ? "ASC"
        : "DESC";
    setFilters((prev) => ({
      ...prev,
      orderBy: column,
      orderDirection: newDirection,
    }));
  };

  const handleToggleIgnored = async (lineupId, newIgnoredStatus) => {
    setToggleIgnoredLoading(lineupId);

    try {
      const response = await put(`/admin/versus/lineups/${lineupId}/ignored`, {
        isIgnored: newIgnoredStatus,
      });

      if (response.success) {
        setLineups((prevLineups) =>
          prevLineups.map((lineup) =>
            lineup.versusGameLineupId === lineupId
              ? { ...lineup, isIgnored: newIgnoredStatus }
              : lineup
          )
        );

        console.log(
          `Versus lineup ${
            newIgnoredStatus ? "ignored" : "activated"
          } successfully`
        );
      } else {
        console.error(
          "Failed to update versus lineup ignored status:",
          response.error
        );
        alert("Failed to update lineup status. Please try again.");
      }
    } catch (error) {
      console.error("Error updating versus lineup ignored status:", error);
      alert("Error updating lineup status. Please try again.");
    } finally {
      setToggleIgnoredLoading(null);
    }
  };

  const handleOpenLineupDialog = (lineupId) => {
    setSelectedLineupId(lineupId);
    setDialogOpen(true);
  };

  const handleCloseLineupDialog = () => {
    setDialogOpen(false);
    setSelectedLineupId(null);
  };

  const handleOpenCardsDialog = (userId, username) => {
    setSelectedUserId(userId);
    setSelectedUsername(username);
    setCardsDialogOpen(true);
  };

  const handleCloseCardsDialog = () => {
    setCardsDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUsername(null);
  };

  useEffect(() => {
    if (isActive) {
      setLineups([]);
      setHasMoreLineups(true);
      loadLineups(0);
      loadFptsStats();
    }
  }, [
    filters.orderBy,
    filters.orderDirection,
    isActive,
    loadLineups,
    loadFptsStats,
  ]);

  useEffect(() => {
    if (isActive) {
      setLineups([]);
      setHasMoreLineups(true);
      loadLineups(0);
    }
  }, [filters.fptsRange, isActive, loadLineups]);
  const getSortIcon = (column) => {
    if (filters.orderBy !== column) return " ⇅";
    return filters.orderDirection === "DESC" ? " ↓" : " ↑";
  };

  const handleSwitchChange = (event, lineupId, currentIgnoredStatus) => {
    event.stopPropagation();
    handleToggleIgnored(lineupId, !currentIgnoredStatus);
  };

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8} lg={8}>
          {fptsStatsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <CircularProgress />
            </Box>
          ) : fptsStats ? (
            <Box sx={{ position: "relative" }}>
              <FptsLineupsChart
                title="Lineup Distribution by FPTS Range"
                subheader={`Total: ${fptsStats.totalLineups} lineups across ${fptsStats.summary.totalRanges} ranges`}
                height={300}
                chartLabels={fptsStats.fptsRanges.map((stat) => stat.range)}
                chartData={[
                  {
                    name: "Lineups",
                    type: "bar",
                    fill: "solid",
                    data: fptsStats.fptsRanges.map((stat) => stat.count),
                  },
                ]}
              />
              <Tooltip title="Refresh FPTS Statistics">
                <IconButton
                  onClick={loadFptsStats}
                  disabled={fptsStatsLoading}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    backgroundColor: "background.paper",
                    boxShadow: 1,
                    "&:hover": {
                      backgroundColor: "background.paper",
                      boxShadow: 2,
                    },
                  }}
                >
                  <Iconify icon="eva:refresh-outline" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 400,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                color: "text.secondary",
              }}
            >
              <Typography>No FPTS statistics available</Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4} lg={4}>
          {fptsStats && <FptsStatsSummary fptsStats={fptsStats} />}
        </Grid>
      </Grid>

      <FilterPanel
        filterPanelExpanded={filterPanelExpanded}
        setFilterPanelExpanded={setFilterPanelExpanded}
        filterInputs={filterInputs}
        handleInputChange={handleInputChange}
        handleApplyFilters={handleApplyFilters}
        handleClearFilters={handleClearFilters}
        filters={filters}
      />

      {lineups === null || lineupsLoading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center" }}
          style={{ padding: 20 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={loadLineups}
          hasMore={hasMoreLineups}
          loader={false}
        >
          <VersusLineupsTableContent
            lineups={lineups}
            filters={filters}
            ё
            toggleIgnoredLoading={toggleIgnoredLoading}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
            handleOpenLineupDialog={handleOpenLineupDialog}
            handleOpenCardsDialog={handleOpenCardsDialog}
            handleSwitchChange={handleSwitchChange}
            loadingMore={loadingMore}
          />
        </InfiniteScroll>
      )}
      <VersusGameLineupDialog
        open={dialogOpen}
        onClose={handleCloseLineupDialog}
        lineupId={selectedLineupId}
      />

      <UserCardsDialog
        open={cardsDialogOpen}
        onClose={handleCloseCardsDialog}
        userId={selectedUserId}
        username={selectedUsername}
      />
    </>
  );
}
